import { AxiosError } from 'axios';
import Api from './index';
import store from '../redux/store';
import { logout } from '../redux/authSlice';

// Every wrapper below always calls these with exactly one argument — the
// axios response, or a synthesized `{ data: { statusCode, message? } }`
// error payload — so callers' own callbacks (typically `(s: any) => {}` /
// `(e: any) => {}` throughout this codebase) stay compatible.
type ApiSuccessCallback = (response: any) => void;
type ApiErrorCallback = (error: any) => void;
type ApiRequestParams = Record<string, unknown>;

export const handleUnauthorisedUser = () => {
  localStorage.clear();
  store.dispatch(logout());
  // window.location.reload();
};

export const apiErrorCode = {
  unauthorized: 401,
  accessDenied: 430,
  sessionExpired: 440,
  validationError: 400,
  emailNotVerified: 403,
};
export const apiSuccessCode = {
  created: 201,
  success: 200,
  accepted: 202,
};

export const checkUserValidation = (statusCode: number) => {
  if (statusCode) {
    return (
      statusCode === apiErrorCode.sessionExpired ||
      statusCode === apiErrorCode.unauthorized ||
      statusCode === apiErrorCode.accessDenied
    );
  }
  return false;
};

/**
 * post api
 *
 * @param params
 * @param endPoint
 * @param errorCalback
 * @param successCallback
 */
const postApiCall = (
  endPoint: string,
  params: ApiRequestParams,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
) => {
  Api.axiosInstance
    .post(endPoint, params)
    .then(response => {
      successCallback(response);
    })
    .catch((error: AxiosError) => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

/**
 * put api
 *
 * @param params
 * @param endPoint
 * @param errorCalback
 * @param successCallback
 */
const putApiCall = (
  endPoint: string,
  params: ApiRequestParams,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
) => {
  Api.axiosInstance
    .put(endPoint, params)
    .then(response => {
      successCallback(response);
    })
    .catch(error => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

/**
 * get api
 *
 * @param endPoint
 * @param errorCalback
 * @param successCallback
 */

const getApiCall = (
  endPoint: string,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
  customHeaders: any = {},
) => {
  Api.axiosInstance
    .get(endPoint, { data: null, headers: customHeaders })
    .then(response => {
      successCallback(response);
    })
    .catch(error => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

const handleApiError = (error: Record<string, any>): ResponseType => {
  const errorResponseData: any = {
    success: false,
    data: {
      message: error?.message || 'Some error encountered',
    },
  };
  // handle API error if its a response error
  if (error?.code) {
    const axiosError = error as AxiosError;
    if (axiosError.message === 'Network Error') {
      // If network error
    } else if (axiosError.code === 'ECONNABORTED') {
      const payload = {
        data: {
          statusCode: 408,
        },
      };
      errorResponseData.data = payload;
    } else if (axiosError.response) {
      if (checkUserValidation(axiosError.response.status)) {
        handleUnauthorisedUser();
      }
      errorResponseData.data = axiosError.response;
    } else if (!axiosError.response) {
      const payload = {
        data: {
          statusCode: '',
        },
      };
      errorResponseData.data = payload;
    }
  }
  return errorResponseData;
};

const getApiCallNew = async (endPoint: string) => {
  try {
    const response = await Api.axiosInstance.get(endPoint);
    return {
      success: true,
      data: response?.data?.data,
      fullData: response?.data,
    };
  } catch (error) {
    const errorResponse = handleApiError(error as Record<string, any>);
    return errorResponse;
  }
};

/**
 * patch api
 *
 * @param params
 * @param endPoint
 * @param errorCalback
 * @param successCallback
 */

const patchApiCall = (
  endPoint: string,
  params: ApiRequestParams,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
) => {
  Api.axiosInstance
    .patch(endPoint, params)
    .then(response => {
      successCallback(response);
    })
    .catch(error => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

/**
 * patch api
 *
 * @param params
 * @param endPoint
 * @param errorCalback
 * @param successCallback
 */

const deleteApiCall = (
  endPoint: string,
  body = {},
  successCallback: ApiSuccessCallback,
  errorCallback: ApiErrorCallback,
  params = '',
) => {
  body = body || {};
  Api.axiosInstance
    .delete(endPoint + params, { data: body })
    .then(response => {
      successCallback(response);
    })
    .catch(error => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            status: 0,
            message: 'Network error',
          },
        };
        errorCallback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            status: 408,
          },
        };
        errorCallback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCallback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            status: '',
          },
        };
        errorCallback(payload);
      }
    });
};

const getApiCallBasicAuth = (
  endPoint: string,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
  customHeaders: any = {},
) => {
  Api.axiosInstanceWithoutToken
    .get(endPoint, { data: null, headers: customHeaders })
    .then(response => {
      successCallback(response);
    })
    .catch(error => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

const postApiCallWithOutToken = (
  endPoint: string,
  params: ApiRequestParams,
  successCallback: ApiSuccessCallback,
  errorCalback: ApiErrorCallback,
) => {
  Api.axiosInstanceWithoutToken
    .post(endPoint, params)
    .then(response => {
      successCallback(response);
    })
    .catch((error: AxiosError) => {
      if (error.message === 'Network Error') {
        const payload = {
          data: {
            statusCode: 0,
            message: 'Network error',
          },
        };
        errorCalback(payload);
      } else if (error.code === 'ECONNABORTED') {
        const payload = {
          data: {
            statusCode: 408,
          },
        };
        errorCalback(payload);
      } else if (error.response) {
        if (checkUserValidation(error.response.status)) {
          handleUnauthorisedUser();
        }
        errorCalback(error.response);
      } else if (!error.response) {
        const payload = {
          data: {
            statusCode: '',
          },
        };
        errorCalback(payload);
      }
    });
};

interface ApiCallItem {
  url: string;
  transformFn?: (data: any) => any; // Optional transform logic
  dispatchFn: (data: any) => any; // Redux action
  cb?: (data?: any) => any;
}

const multiGetApiCallWithDispatch = async (
  calls: ApiCallItem[],
  dispatch: any,
): Promise<void> => {
  const promises = calls.map(
    ({ url, dispatchFn, transformFn, cb }) =>
      new Promise((resolve, reject) => {
        getApiCall(
          url,
          (res: any) => {
            let result = res.data?.data;
            if (transformFn) {
              result = transformFn(result);
            }
            dispatch(dispatchFn(result));
            cb && cb(result);
            resolve(result);
          },
          (err: any) => reject(err),
        );
      }),
  );

  await Promise.all(promises);
};

// utils/parallelApi.ts
const callParallelApis = async (
  tasks: Array<() => Promise<any> | any>,
  onComplete?: (results: any[]) => void,
) => {
  try {
    const results = await Promise.all(tasks.map(fn => fn()));
    onComplete?.(results);
  } catch (error) {
    console.error('Error in parallel API calls:', error);
  }
};

export {
  getApiCall,
  putApiCall,
  postApiCall,
  patchApiCall,
  deleteApiCall,
  postApiCallWithOutToken,
  getApiCallNew,
  multiGetApiCallWithDispatch,
  callParallelApis,
  getApiCallBasicAuth,
};
